import React from 'react';
import { Users, Brain, Globe, Shield, Target, BookOpen, CheckCircle } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <SEO 
        title="About Us - AI Wealth Manager"
        description="Learn about our founding team of fintech executives, AI architects, and engineers committed to democratizing institutional-quality investment advice."
        keywords="AI wealth management team, fintech executives, financial technology, investment advisors, artificial intelligence"
      />
      
      <Navbar />
      
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="px-4 py-16">
          <div className="container mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-[#9E7C47] to-[#F9F1A9] bg-clip-text text-transparent">
              About AI Wealth Manager
            </h1>
            <p className="text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
              We're democratizing access to institutional-quality investment advice through AI-first solutions 
              backed by human expertise when needed.
            </p>
          </div>
        </section>

        {/* Mission & Values */}
        <section className="px-4 py-16 bg-black/50">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gradient">Our Mission & Values</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="glass-card rounded-xl p-6 text-center hover:border-[#9E7C47]/50 transition-all duration-300">
                <Target className="h-12 w-12 text-[#9E7C47] mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-3">Democratizing Access</h3>
                <p className="text-white/70 text-sm">
                  Making institutional-quality investment advice accessible to everyone, not just the ultra-wealthy.
                </p>
              </div>
              
              <div className="glass-card rounded-xl p-6 text-center hover:border-[#9E7C47]/50 transition-all duration-300">
                <Shield className="h-12 w-12 text-[#F9F1A9] mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-3">Trust & Transparency</h3>
                <p className="text-white/70 text-sm">
                  Committed to transparency, regulatory compliance, and investor protection in everything we do.
                </p>
              </div>
              
              <div className="glass-card rounded-xl p-6 text-center hover:border-[#9E7C47]/50 transition-all duration-300">
                <Brain className="h-12 w-12 text-[#9E7C47] mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-3">AI-First Innovation</h3>
                <p className="text-white/70 text-sm">
                  Building cutting-edge AI solutions backed by human expertise when needed for optimal results.
                </p>
              </div>
              
              <div className="glass-card rounded-xl p-6 text-center hover:border-[#9E7C47]/50 transition-all duration-300">
                <BookOpen className="h-12 w-12 text-[#F9F1A9] mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-3">Financial Education</h3>
                <p className="text-white/70 text-sm">
                  Focused on financial education and empowering informed investment decisions for our users.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Founding Team */}
        <section className="px-4 py-16">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gradient">Our Founding Team</h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                Built by experienced professionals who understand both technology and finance
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="glass-card rounded-xl p-8 bg-gradient-to-br from-[#9E7C47]/10 to-[#F9F1A9]/10 border border-[#9E7C47]/20">
                <div className="flex items-center mb-6">
                  <Users className="h-10 w-10 text-[#9E7C47] mr-4" />
                  <h3 className="text-2xl font-bold">Leadership Background</h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#9E7C47] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-white/80">Founded by former fintech executives with experience at multiple unicorns</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#9E7C47] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-white/80">Team includes MBA graduates from top global business schools</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#9E7C47] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-white/80">Deep understanding of both traditional finance and modern technology</span>
                  </li>
                </ul>
              </div>
              
              <div className="glass-card rounded-xl p-8 bg-gradient-to-br from-[#F9F1A9]/10 to-[#9E7C47]/10 border border-[#F9F1A9]/20">
                <div className="flex items-center mb-6">
                  <Brain className="h-10 w-10 text-[#F9F1A9] mr-4" />
                  <h3 className="text-2xl font-bold">Technical Expertise</h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#F9F1A9] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-white/80">Led by AI architects with experience at leading financial intelligence companies</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#F9F1A9] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-white/80">Core team includes former engineers from top-tier financial technology firms</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#F9F1A9] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-white/80">Expertise in machine learning, natural language processing, and financial modeling</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Global Presence */}
        <section className="px-4 py-16 bg-black/50">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gradient">Global Presence</h2>
            </div>
            
            <div className="max-w-2xl mx-auto">
              <div className="glass-card rounded-xl p-8 text-center bg-gradient-to-br from-[#9E7C47]/10 to-[#F9F1A9]/10 border border-[#9E7C47]/20">
                <Globe className="h-16 w-16 text-[#9E7C47] mx-auto mb-6" />
                <h3 className="text-2xl font-bold mb-4">Headquartered in India</h3>
                <p className="text-white/80 text-lg mb-6">
                  With global technology partnerships that enable us to serve clients worldwide
                </p>
                <div className="inline-flex items-center px-6 py-3 rounded-full bg-[#9E7C47]/20 border border-[#9E7C47]/30">
                  <span className="text-[#F9F1A9] font-semibold">Building for the global market</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="px-4 py-16">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gradient">Why Choose AI Wealth Manager</h2>
              <p className="text-lg text-white/70 max-w-3xl mx-auto">
                Our unique combination of cutting-edge technology and human expertise sets us apart
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="glass-card rounded-xl p-6 hover:border-[#9E7C47]/50 transition-all duration-300">
                <Brain className="h-12 w-12 text-[#9E7C47] mb-4" />
                <h3 className="text-xl font-semibold mb-3">AI-Powered Intelligence</h3>
                <p className="text-white/70">
                  Advanced algorithms analyze markets 24/7, providing insights and recommendations based on real-time data.
                </p>
              </div>
              
              <div className="glass-card rounded-xl p-6 hover:border-[#9E7C47]/50 transition-all duration-300">
                <Users className="h-12 w-12 text-[#F9F1A9] mb-4" />
                <h3 className="text-xl font-semibold mb-3">Human Expertise</h3>
                <p className="text-white/70">
                  When you need personalized guidance, our certified wealth managers are here to help with complex decisions.
                </p>
              </div>
              
              <div className="glass-card rounded-xl p-6 hover:border-[#9E7C47]/50 transition-all duration-300">
                <Shield className="h-12 w-12 text-[#9E7C47] mb-4" />
                <h3 className="text-xl font-semibold mb-3">Trust & Security</h3>
                <p className="text-white/70">
                  Bank-level security, regulatory compliance, and transparent practices ensure your investments are protected.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default AboutUs;